import React, { Key, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import arrowLeft from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/arrow-left.png'
import arrowRight from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/arrow-right.svg'
import fillingStationsService, {
  FillingStation,
} from '../../../../../../BoostXpressAdmin/Services/fillingStationsService'
import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'
import { HorizontalLoader } from 'Components'
import LinkFuelFactModal from './LinkFuelFactModal'

import './StationsList.scss'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import Swal from 'sweetalert2'

const StationsList = () => {
  const navigate = useNavigate()

  const [pageNumber, setPageNumber] = useState(1)
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [stations, setStations] = useState<FillingStation[]>([])

  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [isLinkFuelFactModalOpen, setIsLinkFuelFactModalOpen] = useState(false)
  const [isUnLinkFuelFactModalOpen, setIsUnLinkFuelFactModalOpen] = useState(false)
  const [selectedStation, setSelectedStation] = useState<FillingStation | null>(null)

  // Filter states
  const [showFilters, setShowFilters] = useState(false)
  const [stationId, setStationId] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [branchName, setBranchName] = useState('')
  const [accountManagerId, setAccountManagerId] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)

  useEffect(() => {
    let cancelled = false
    const t = window.setTimeout(async () => {
      try {
        setIsLoading(true)
        // Build filter parameters
        const params: any = {
          pageNumber,
          sortOrder: 'DESC',
        }

        // Add search if exists
        if (search) {
          // Check if it's a station ID (numeric)
          const isNumeric = /^\d+$/.test(search.trim())
          if (isNumeric) {
            params.id = search.trim()
          } else {
            params.businessName = search
          }
        }

        // Add filters
        if (stationId) params.id = stationId
        if (businessName) params.businessName = businessName
        if (branchName) params.branchName = branchName
        if (accountManagerId) params.accountManagerId = accountManagerId
        if (includeDeleted) params.includeDeleted = includeDeleted

        const res = await fillingStationsService.list(params)
        if (cancelled) return
        setStations(res.data?.dataset ?? [])
        setTotal(res.data?.total ?? 0)
        setPageSize(res.data?.pageSize ?? 10)
      } catch (e) {
        if (!cancelled) {
          setStations([])
          setTotal(0)
          setPageSize(10)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [pageNumber, search, stationId, businessName, branchName, accountManagerId, includeDeleted])

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-station-menu-root="true"]')) return
      setOpenMenuId(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  const totalPages = useMemo(() => {
    if (!total) return 1
    return Math.max(1, Math.ceil(total / pageSize))
  }, [pageSize, total])


  const getFuelTypesLabel = (s: FillingStation) => {
    const fromProducts = (s.products ?? []).map((product) => product.product).filter(Boolean)
    const fromPumps = (s.fuelPumps ?? []).map((pump) => pump.productType)
    const types = fromProducts.length ? fromProducts : fromPumps
    const unique = Array.from(new Set(types))
    return unique.length ? unique.join(', ') : '-'
  }

  const refreshList = async () => {
    const res = await fillingStationsService.list({
      pageNumber,
      businessName: search ? search : undefined,
      sortOrder: 'DESC',
      includeDeleted: false,
    })
    setStations(res.data?.dataset ?? [])
    setTotal(res.data?.total ?? 0)
    setPageSize(res.data?.pageSize ?? 10)
  }

  const handleDeactivate = async (id: string) => {
    const ok = window.confirm('Deactivate this station?')
    if (!ok) return
    try {
      await fillingStationsService.remove(id)
      await refreshList()
    } catch (e) {
      window.alert('Failed to deactivate station.')
    }
  }

  const handleDelete = async (id: string) => {
    const ok = window.confirm('Delete this station?')
    if (!ok) return
    try {
      await fillingStationsService.remove(id)
      await refreshList()
    } catch (e) {
      window.alert('Failed to delete station.')
    }
  }

  const handleLinkFuelFact = (station: FillingStation) => {
    setSelectedStation(station)
    setIsLinkFuelFactModalOpen(true)
    setOpenMenuId(null)
  }
  const handleUnLinkFuelFact = async (station: FillingStation) => {
    Swal.fire({
      title: 'Confirm!',
      text: 'Are you sure you want to unlink Fuel Fact from this station?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#186AFF',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, unlink!',
    }).then(async (result) => {
      if (!result.isConfirmed) return Swal.showLoading()
      await fillingStationsService.unlinkFF(station.id).then(
        () => {
          refreshList()
          Swal.close()
          handleSuccess('FuelFact information unlinked from station successfully')
        },
        (err) => {
          Swal.close()
          handleError(err)
        },
      )
    })
  }

  const handleCloseLinkFuelFactModal = () => {
    setIsLinkFuelFactModalOpen(false)
    setSelectedStation(null)
  }

  const handleLinkFuelFactSuccess = () => {
    refreshList()
  }

  const handleTogglePublic = async (station: FillingStation) => {
    const newStatus = !station.isPublic
    const confirmMessage = newStatus ? 'Make this station public?' : 'Make this station private?'

    if (!window.confirm(confirmMessage)) return

    try {
      await fillingStationsService.update(station.id, { isPublic: newStatus } as any)
      await refreshList()
    } catch (e) {
      window.alert('Failed to update station visibility.')
    }
  }

  return (
    <div className='bx-stations-list'>
      <div className='bx-stations-list__header'>
        <h2 className='bx-stations-list__title'>Station Management</h2>
        <button
          className='bx-stations-list__add-btn'
          onClick={() => navigate('/boostxpress/stations/new')}
        >
          + Add Station
        </button>
      </div>

      <div className='bx-stations-list__content'>
        <div className='bx-stations-list__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-8 col-lg-8 col-xxl-10'>
            <div className='bx-stations-search w-100'>
              <div className='bx-stations-search__icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                className='bx-stations-search__input'
                placeholder='Search Station ID or Business Name'
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPageNumber(1)
                }}
              />
            </div>
          </div>

          <div className='col-6 col-md-2 col-lg-2 col-xxl-1 position-relative'>
            <button
              className='bx-stations-actions__btn w-100'
              onClick={() => setShowFilters(!showFilters)}
            >
              <img src={filterIcon} alt='' className='bx-stations-actions__icon' />
              <span className='bx-stations-actions__label'>Filters</span>
            </button>

            {showFilters && (
              <div className='bx-stations-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Stations</h5>
                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Station ID</label>
                    <input
                      type='text'
                      value={stationId}
                      onChange={(e) => setStationId(e.target.value)}
                      className='form-control'
                      placeholder='Enter Station ID'
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>Business Name</label>
                    <input
                      type='text'
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className='form-control'
                      placeholder='Enter Business Name'
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>Branch Name</label>
                    <input
                      type='text'
                      value={branchName}
                      onChange={(e) => setBranchName(e.target.value)}
                      className='form-control'
                      placeholder='Enter Branch Name'
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>Account Manager ID</label>
                    <input
                      type='text'
                      value={accountManagerId}
                      onChange={(e) => setAccountManagerId(e.target.value)}
                      className='form-control'
                      placeholder='Enter Manager ID'
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>
                      <input
                        type='checkbox'
                        checked={includeDeleted}
                        onChange={(e) => setIncludeDeleted(e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      Include Deleted Stations
                    </label>
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    className='btn btn-secondary'
                    onClick={() => {
                      setStationId('')
                      setBusinessName('')
                      setBranchName('')
                      setAccountManagerId('')
                      setIncludeDeleted(false)
                    }}
                  >
                    Clear Filters
                  </button>
                  <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className='col-6 col-md-2 col-lg-2 col-xxl-1'>
            <button className='bx-stations-actions__btn w-100'>
              <img src={exportIcon} alt='' className='bx-stations-actions__icon' />
              <span className='bx-stations-actions__label'>Export</span>
            </button>
          </div>
        </div>

        <div className='bx-stations-table'>
          <div className='bx-table-wrapper'>
            <table className='bx-stations-table__table'>
              <thead>
                <tr>
                  <th className='bx-stations-table__th bx-stations-table__th--name'>S/N</th>
                  <th className='bx-stations-table__th bx-stations-table__th--name'>
                    Station Name
                  </th>
                  <th className='bx-stations-table__th bx-stations-table__th--city'>City</th>
                  <th className='bx-stations-table__th bx-stations-table__th--vol'>Manager</th>
                  <th className='bx-stations-table__th bx-stations-table__th--seen'>State</th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>Status</th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>
                    Visibility
                  </th>
                  <th className='bx-stations-table__th bx-stations-table__th--status'>
                    Date Created
                  </th>
                  <th className='bx-stations-table__th bx-stations-table__th--actions'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td className='bx-stations-table__td' colSpan={8}>
                      <HorizontalLoader></HorizontalLoader>
                    </td>
                  </tr>
                ) : stations.length === 0 ? (
                  <tr>
                    <td className='bx-stations-table__td' colSpan={8}>
                      No stations found.
                    </td>
                  </tr>
                ) : (
                  stations.map((s, index: Key) => (
                    <tr key={s.id}>
                      <td className='bx-stations-table__td bx-stations-table__td--name'>
                        {Number(index) + 1 + (pageNumber - 1) * pageSize}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--name'>
                        {s.branchName ? `${s.businessName} - ${s.branchName}` : s.businessName}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--city'>
                        {s.city || '-'}
                      </td>

                      <td className='bx-stations-table__td bx-stations-table__td--vol'>
                        {s.accountManager?.firstName} {s.accountManager?.lastName}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--seen'>
                        {s.state}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--status'>
                        <div className='bx-stations-table__badge-wrap'>
                          <span className='bx-stations-table__badge'>Active</span>
                        </div>
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--seen'>
                        <span
                          className={`bx-stations-table__public-badge ${s.isPublic ? 'bx-stations-table__public-badge--public' : 'bx-stations-table__public-badge--private'}`}
                        >
                          {s.isPublic ? 'Public' : 'Private'}
                        </span>
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--seen'>
                        {DateTimeFormatter(s.createdAtISO8601)}
                      </td>
                      <td className='bx-stations-table__td bx-stations-table__td--actions'>
                        <div className='bx-stations-table__menu' data-station-menu-root='true'>
                          <button
                            type='button'
                            className='bx-stations-table__menu-btn'
                            aria-haspopup='menu'
                            aria-expanded={openMenuId === s.id}
                            onClick={() => setOpenMenuId((v) => (v === s.id ? null : s.id))}
                          >
                            <svg
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <circle cx='10' cy='4.5' r='1.3' fill='#667085' />
                              <circle cx='10' cy='10' r='1.3' fill='#667085' />
                              <circle cx='10' cy='15.5' r='1.3' fill='#667085' />
                            </svg>
                          </button>

                          {openMenuId === s.id ? (
                            <div className='bx-stations-table__menu-dropdown' role='menu'>
                              <button
                                type='button'
                                className='bx-stations-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  setOpenMenuId(null)
                                  navigate(`/boostxpress/stations/${s.id}`)
                                }}
                              >
                                View
                              </button>
                              <button
                                type='button'
                                className='bx-stations-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  setOpenMenuId(null)
                                  navigate(`/boostxpress/stations/${s.id}/edit`)
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type='button'
                                className='bx-stations-table__menu-item bx-stations-table__menu-item--accent'
                                role='menuitem'
                                onClick={async () => {
                                  setOpenMenuId(null)
                                  await handleDeactivate(s.id)
                                }}
                              >
                                Deactivate
                              </button>
                              <button
                                type='button'
                                className='bx-stations-table__menu-item bx-stations-table__menu-item--danger'
                                role='menuitem'
                                onClick={async () => {
                                  setOpenMenuId(null)
                                  await handleDelete(s.id)
                                }}
                              >
                                Delete
                              </button>

                              <button
                                type='button'
                                className='bx-stations-table__menu-item'
                                role='menuitem'
                                onClick={() => handleLinkFuelFact(s)}
                              >
                                Link Fuel Fact
                              </button>

                              <button
                                type='button'
                                className='bx-stations-table__menu-item'
                                role='menuitem'
                                onClick={() => handleUnLinkFuelFact(s)}
                              >
                                UnLink Fuel Fact
                              </button>
                              <button
                                type='button'
                                className='bx-stations-table__menu-item'
                                role='menuitem'
                                onClick={async () => {
                                  setOpenMenuId(null)
                                  await handleTogglePublic(s)
                                }}
                              >
                                {s.isPublic ? 'Make Private' : 'Make Public'}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className='bx-stations-pagination'>
              <button
                className='bx-stations-pagination__btn'
                disabled={pageNumber <= 1 || isLoading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <div className='bx-stations-pagination__btn-icon-wrap'>
                  <img
                    src={arrowLeft}
                    alt=''
                    className='bx-stations-pagination__btn-icon bx-stations-pagination__btn-icon--left'
                  />
                </div>
                <span className='bx-stations-pagination__btn-text'>Previous</span>
              </button>

              <div className='bx-stations-pagination__numbers'>
                <span className='bx-stations-pagination__number bx-stations-pagination__number--active'>
                  {pageNumber}
                </span>
                <span className='bx-stations-pagination__ellipsis'>/</span>
                <span className='bx-stations-pagination__number'>{totalPages}</span>
              </div>

              <button
                className='bx-stations-pagination__btn'
                disabled={pageNumber >= totalPages || isLoading}
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              >
                <span className='bx-stations-pagination__btn-text'>Next</span>
                <img src={arrowRight} alt='' className='bx-stations-pagination__btn-icon' />
              </button>
            </div>
          </div>
        </div>
      </div>

      <LinkFuelFactModal
        isOpen={isLinkFuelFactModalOpen}
        onClose={handleCloseLinkFuelFactModal}
        onSuccess={handleLinkFuelFactSuccess}
        stationId={selectedStation?.id || ''}
        station={selectedStation}
      />
      <ToastContainer></ToastContainer>
    </div>
  )
}

export default StationsList
